import React, { useEffect, useState } from 'react';
import { GameCard } from '../components/GameCard';
import { Modal } from '../components/Modal';
import { api } from '../services/api';
import { Game } from '../types';
import { Search, Filter, Gamepad2, Play, ExternalLink, Tag, Star } from 'lucide-react';

export const GamesPage: React.FC = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const data = await api.getGames();
        setGames(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchGames();
  }, []);

  // Filter list
  const genres = ['All', ...Array.from(new Set(games.map((g) => g.genre)))];
  const statuses = ['All', 'Released', 'Early Access', 'In Development'];

  const filteredGames = games.filter((game) => {
    const matchesSearch =
      game.title.toLowerCase().includes(search.toLowerCase()) ||
      game.description.toLowerCase().includes(search.toLowerCase()) ||
      (game.tags && game.tags.some((t) => t.toLowerCase().includes(search.toLowerCase())));

    const matchesGenre = selectedGenre === 'All' || game.genre === selectedGenre;
    const matchesStatus = selectedStatus === 'All' || game.status === selectedStatus;

    return matchesSearch && matchesGenre && matchesStatus;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12 pb-24">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-950/60 border border-cyan-500/30 text-cyan-300 text-xs font-semibold">
          <Gamepad2 className="w-3.5 h-3.5 text-cyan-400" />
          <span>GSR Portfolio</span>
        </div>
        <h1 className="text-4xl font-black text-white tracking-tight">Our Games</h1>
        <p className="text-slate-400 text-sm leading-relaxed">
          Explore our complete universe of titles. From competitive anti-gravity racers to sprawling narrative-driven RPGs, experience our creations.
        </p>
      </div>

      {/* Filter / Search Bar */}
      <div className="bg-slate-900/60 rounded-2xl border border-slate-800 p-4 space-y-4 md:space-y-0 md:flex md:items-center md:justify-between gap-4">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by game title, mechanics, or tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition"
          />
        </div>

        {/* Dropdowns */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500 hidden sm:block" />
            <select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
              className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:outline-none focus:border-cyan-500/50 transition cursor-pointer"
            >
              {genres.map((g) => (
                <option key={g} value={g}>
                  Genre: {g}
                </option>
              ))}
            </select>
          </div>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:outline-none focus:border-cyan-500/50 transition cursor-pointer"
          >
            {statuses.map((s) => (
              <option key={s} value={s}>
                Status: {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Games Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-96 rounded-2xl bg-slate-900/50 animate-pulse border border-slate-800" />
          ))}
        </div>
      ) : filteredGames.length === 0 ? (
        <div className="text-center py-20 bg-slate-900/30 rounded-3xl border border-dashed border-slate-800 space-y-3">
          <Gamepad2 className="w-10 h-10 text-slate-600 mx-auto" />
          <h3 className="text-lg font-bold text-white">No games found</h3>
          <p className="text-slate-500 text-xs">
            Try adjusting your search criteria or resetting filters.
          </p>
          <button
            onClick={() => {
              setSearch('');
              setSelectedGenre('All');
              setSelectedStatus('All');
            }}
            className="px-4 py-2 text-xs font-semibold text-cyan-400 hover:underline"
          >
            Clear all filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredGames.map((game) => (
            <GameCard key={game.id} game={game} onOpenDetails={(g) => setSelectedGame(g)} />
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Modal
        isOpen={Boolean(selectedGame)}
        onClose={() => setSelectedGame(null)}
        title={selectedGame?.title || 'Game Details'}
      >
        {selectedGame && (
          <div className="space-y-6">
            <div className="relative aspect-video w-full rounded-2xl overflow-hidden bg-slate-950">
              <img
                src={selectedGame.banner_url || selectedGame.thumbnail_url}
                alt={selectedGame.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-3 right-3 flex items-center gap-1 bg-slate-950/80 backdrop-blur-md px-3 py-1 rounded-full border border-slate-800 text-amber-400 text-xs font-bold">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                <span>{selectedGame.rating.toFixed(1)} / 5.0</span>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-cyan-950 text-cyan-300 border border-cyan-800">
                  {selectedGame.genre}
                </span>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-slate-800 text-slate-300 border border-slate-700">
                  Status: {selectedGame.status}
                </span>
              </div>

              <h4 className="text-xl font-bold text-white">{selectedGame.title}</h4>
              <p className="text-slate-300 text-sm leading-relaxed">{selectedGame.description}</p>
            </div>

            {selectedGame.tags && selectedGame.tags.length > 0 && (
              <div>
                <h5 className="text-xs font-semibold uppercase text-slate-400 tracking-wider mb-2">
                  Tags & Features
                </h5>
                <div className="flex flex-wrap gap-2">
                  {selectedGame.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="text-xs px-2.5 py-1 rounded-lg bg-slate-800/80 text-slate-300 border border-slate-700/60 flex items-center gap-1.5"
                    >
                      <Tag className="w-3 h-3 text-cyan-400" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-slate-800 flex flex-col sm:flex-row gap-3">
              {selectedGame.play_url && (
                <a
                  href={selectedGame.play_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-slate-950 font-bold text-sm flex items-center justify-center gap-2 transition"
                >
                  <Play className="w-4 h-4 fill-current" />
                  <span>Launch / Play</span>
                </a>
              )}
              {selectedGame.download_url && (
                <a
                  href={selectedGame.download_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 py-3 px-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm flex items-center justify-center gap-2 transition border border-slate-700"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Store Page</span>
                </a>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
